import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { Env } from "../../../styles";
import "./style.css";

const { RangePicker } = DatePicker;
const { Text } = Typography;

/** استخراج اسم نوع الإجازة من شكل الاستجابة (يختلف حسب الـ API) */
function pickVacationTypeLabel(vac, labelById) {
  const textFields = [
    vac?.vac_type_name,
    vac?.vactype,
    vac?.type_name,
    vac?.types,
    vac?.name,
    vac?.task_type,
    vac?.vacation_type,
    vac?.vacationtype_name,
    vac?.vacationtype?.name,
    vac?.vacationType?.name,
    vac?.label,
  ];
  for (const f of textFields) {
    if (f != null && String(f).trim() !== "") return String(f).trim();
  }
  const idCandidates = [
    vac?.vacationtype_id,
    vac?.vacationtype,
    vac?.vac_id,
    vac?.vid,
  ];
  if (Number(vac?.type) > 0) idCandidates.push(vac.type);
  for (const raw of idCandidates) {
    const n = Number(raw);
    if (!Number.isNaN(n) && n > 0 && labelById[n]) return labelById[n];
  }
  return "";
}

export default function AttendanceLogs(props) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const [filters, setFilters] = useState({
    user_id: null,
  });

  const [vacationsByUser, setVacationsByUser] = useState({});
  const [vacDetailOpen, setVacDetailOpen] = useState(false);
  const [vacDetailPayload, setVacDetailPayload] = useState(null);
  const [vacTypeLabelById, setVacTypeLabelById] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    axios
      .get(Env.HOST_SERVER_NAME + "get-tasks-types")
      .then((res) => {
        const m = {};
        (res.data || []).forEach((t) => {
          const id = Number(t.value);
          if (!Number.isNaN(id) && t.label != null && String(t.label).trim() !== "") {
            m[id] = String(t.label).trim();
          }
        });
        setVacTypeLabelById((prev) => ({ ...m, ...prev }));
      })
      .catch(() => {});
  }, []);

  // Initialize dates based on settings
  useEffect(() => {
    if (props.setting && props.setting.length) {
      const monthStart = props.setting.find((i) => i.key == "admin.month_start")?.value || "01";
      const monthEnd = props.setting.find((i) => i.key == "admin.month_end")?.value || "30";
      
      const s = dayjs(dayjs().format("YYYY-MM") + "-" + monthStart, "YYYY-MM-DD")
        .subtract(1, "months")
        .format("YYYY-MM-DD");
      const e = dayjs(dayjs().format("YYYY-MM") + "-" + monthEnd, "YYYY-MM-DD").format("YYYY-MM-DD");
      
      setStart(s);
      setEnd(e);
    }
  }, [props.setting]);

  useEffect(() => {
    if (start && end) {
      fetchLogs();
    }
  }, [start, end, filters.user_id]);

  useEffect(() => {
    if (!data.length || !start || !end) {
      setVacationsByUser({});
      return;
    }
    const uniqueIds = [...new Set(data.map((r) => r.user_id).filter(Boolean))];
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          uniqueIds.map((uid) =>
            axios
              .get(
                `${Env.HOST_SERVER_NAME}dawam-info/${uid}/${start}/${end}`,
              )
              .then((res) => {
                const vt = res.data?.vacstypes;
                const extraNames = {};
                if (Array.isArray(vt)) {
                  vt.forEach((t) => {
                    const id = Number(t.id);
                    if (!Number.isNaN(id) && t.name != null && String(t.name).trim() !== "") {
                      extraNames[id] = String(t.name).trim();
                    }
                  });
                }
                return {
                  uid,
                  list: Array.isArray(res.data?.vacs_list)
                    ? res.data.vacs_list
                    : [],
                  extraNames,
                };
              })
              .catch(() => ({ uid, list: [], extraNames: {} })),
          ),
        );
        if (cancelled) return;
        const mergedNames = {};
        results.forEach((r) => {
          Object.assign(mergedNames, r.extraNames || {});
        });
        if (Object.keys(mergedNames).length > 0) {
          setVacTypeLabelById((prev) => ({ ...prev, ...mergedNames }));
        }
        const map = {};
        results.forEach(({ uid, list }) => {
          map[uid] = list;
        });
        setVacationsByUser(map);
      } catch {
        if (!cancelled) setVacationsByUser({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data, start, end]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        Env.HOST_SERVER_NAME + "get-users-factor-data"
      );
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { user_id } = filters;
      // Use the new URL-segment based endpoint
      const url = Env.HOST_SERVER_NAME + `attendancelogs-list/${start}/${end}${user_id ? `/${user_id}` : ""}`;
      
      const response = await axios.get(url);
      setData(response.data.data);
    } catch (error) {
      notification.error({ message: "فشل تحميل البيانات" });
    } finally {
      setLoading(false);
    }
  };

  const onMonthChange = (all, data) => {
    const monthStart = props.setting.find((i) => i.key == "admin.month_start")?.value || "01";
    const monthEnd = props.setting.find((i) => i.key == "admin.month_end")?.value || "30";

    const s = dayjs(data + "-" + monthStart, "YYYY-MM-DD")
      .subtract(1, "months")
      .format("YYYY-MM-DD");
    const e = dayjs(data + "-" + monthEnd, "YYYY-MM-DD").format("YYYY-MM-DD");
    
    setStart(s);
    setEnd(e);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const onRangeChange = (all, dates) => {
    setStart(dates[0]);
    setEnd(dates[1]);
  };

  useEffect(() => {
    if (isModalVisible) {
      if (editingRecord) {
        form.setFieldsValue({
          user_id: editingRecord.user_id,
          date: editingRecord.date ? dayjs(editingRecord.date) : null,
          attendance_time: editingRecord.attendance_time ? dayjs(editingRecord.attendance_time) : null,
          leave_time: editingRecord.leave_time ? dayjs(editingRecord.leave_time) : null,
          type: editingRecord.type,
          canceled: editingRecord.canceled || 0,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ type: "0", canceled: 0 });
      }
    }
  }, [isModalVisible, editingRecord, form]);

  const showModal = (record = null) => {
    setEditingRecord(record);
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        date: values.date.format("YYYY-MM-DD"),
        attendance_time: values.attendance_time?.format("YYYY-MM-DD HH:mm:ss"),
        leave_time: values.leave_time?.format("YYYY-MM-DD HH:mm:ss"),
      };

      if (editingRecord) {
        await axios.put(
          Env.HOST_SERVER_NAME + `attendancelogs/${editingRecord.id}`,
          payload
        );
        notification.success({ message: "تم تحديث السجل بنجاح" });
      } else {
        await axios.post(Env.HOST_SERVER_NAME + "attendancelogs", payload);
        notification.success({ message: "تم إضافة السجل بنجاح" });
      }
      setIsModalVisible(false);
      fetchLogs();
    } catch (error) {
      notification.error({ message: "فشل حفظ البيانات" });
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(Env.HOST_SERVER_NAME + `attendancelogs/${id}`);
      notification.success({ message: "تم حذف السجل بنجاح" });
      fetchLogs();
    } catch (error) {
      notification.error({ message: "فشل حذف السجل" });
    }
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`بحث ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            بحث
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            إعادة تعيين
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const val = dataIndex.reduce((obj, key) => obj?.[key], record);
      return val ? val.toString().toLowerCase().includes(value.toLowerCase()) : "";
    },
  });

  const getMatchingVacations = (userId, dateStr) => {
    const list = vacationsByUser[userId] || [];
    const currentDay = dayjs(dateStr);
    return list.filter((v) => {
      if (!v.date_from || !v.date_to) return false;
      const vacStart = dayjs(v.date_from).startOf("day");
      const vacEnd = dayjs(v.date_to).endOf("day");
      return (
        currentDay.isSame(vacStart, "day") ||
        currentDay.isSame(vacEnd, "day") ||
        (currentDay.isAfter(vacStart) && currentDay.isBefore(vacEnd))
      );
    });
  };

  /** نفس منطق دمج الإجازات في attendance-table (mergeAttendanceData) */
  const getVacationRowMeta = (userId, dateStr) => {
    const matchingVacations = getMatchingVacations(userId, dateStr);
    const approvedVacations = matchingVacations.filter(
      (v) => Number(v.accepted) === 2,
    );
    const vac_status =
      approvedVacations[0]?.accepted ?? matchingVacations[0]?.accepted;
    const have_vac = matchingVacations.length > 0 ? "1" : "0";
    return { matchingVacations, vac_status, have_vac };
  };

  const columns = [
    {
      title: "اليوم",
      dataIndex: "date",
      key: "dayName",
      width: 100,
      render: (date) => {
        const days = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
        return days[dayjs(date).day()];
      },
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: "الموظف",
      dataIndex: ["user", "name"],
      key: "userName",
      render: (text, record) => text || record.user_id,
      ...getColumnSearchProps(["user", "name"], "الموظف"),
      sorter: (a, b) => (a.user?.name || "").localeCompare(b.user?.name || ""),
    },
    {
      title: "التاريخ",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: "وقت الحضور",
      dataIndex: "attendance_time",
      key: "attendance_time",
      render: (val) => val ? dayjs(val).format("YYYY-MM-DD HH:mm:ss") : "-",
      ...getColumnSearchProps(["attendance_time"], "وقت الحضور"),
      sorter: (a, b) => dayjs(a.attendance_time).unix() - dayjs(b.attendance_time).unix(),
    },
    {
      title: "وقت الانصراف",
      dataIndex: "leave_time",
      key: "leave_time",
      render: (val) => val ? dayjs(val).format("YYYY-MM-DD HH:mm:ss") : "-",
      ...getColumnSearchProps(["leave_time"], "وقت الانصراف"),
      sorter: (a, b) => dayjs(a.leave_time).unix() - dayjs(b.leave_time).unix(),
    },
    {
      title: "المدة",
      dataIndex: "netPeriod",
      key: "netPeriod",
      sorter: (a, b) => (a.netPeriod || "").localeCompare(b.netPeriod || ""),
    },
    {
      title: "النوع",
      dataIndex: "type",
      key: "type",
      filters: Array.from(new Set(data.map(item => item.type))).filter(Boolean).map(t => ({ text: t, value: t })),
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "الحالة",
      dataIndex: "canceled",
      key: "canceled",
      filters: [
        { text: "نشط", value: 0 },
        { text: "ملغى", value: 1 },
      ],
      onFilter: (value, record) => record.canceled == value,
      render: (canceled) => (canceled == 1 ? "ملغى" : "نشط"),
    },
    {
      title: "الإجازات",
      key: "have_vac",
      width: 80,
      align: "center",
      render: (_, record) => {
        const { matchingVacations, vac_status, have_vac } =
          getVacationRowMeta(record.user_id, record.date);
        let statusText = "في انتظار الاعتماد";
        let color = "#8c8c8c";
        if (vac_status == 4) {
          color = "#991b1b";
          statusText = "مرفوضة من الشؤون";
        } else if (vac_status == 3) {
          color = "#f87171";
          statusText = "مرفوضة من مدير الإدارة";
        } else if (vac_status == 2) {
          color = "#0972B6";
          statusText = "معتمدة من الشؤون";
        } else if (vac_status == 1) {
          color = "#52c41a";
          statusText = "معتمدة من مدير الإدارة";
        }
        if (!(have_vac == "1" || vac_status != null)) return null;
        return (
          <div
            style={{ cursor: "pointer", textAlign: "center" }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setVacDetailPayload({
                  record,
                  matching: matchingVacations,
                  statusText,
                  color,
                });
                setVacDetailOpen(true);
              }
            }}
            onClick={() => {
              setVacDetailPayload({
                record,
                matching: matchingVacations,
                statusText,
                color,
              });
              setVacDetailOpen(true);
            }}
          >
            <CopyOutlined
              style={{
                marginLeft: "10px",
                color,
                fontSize: vac_status > 0 ? "18px" : "16px",
              }}
            />
          </div>
        );
      },
    },
    {
      title: "المنطقة الزمنية",
      dataIndex: "timezone",
      key: "timezone",
      ellipsis: true,
    },
    {
      title: "الإجراءات",
      key: "actions",
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            type="link"
          />
          <Popconfirm
            title="هل أنت متأكد من الحذف؟"
            onConfirm={() => handleDelete(record.id)}
            okText="نعم"
            cancelText="لا"
          >
            <Button icon={<DeleteOutlined />} type="link" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handlePasswordSubmit = () => {
    if (passwordInput === "777414709") {
      setIsAuthenticated(true);
    } else {
      notification.error({ message: "رمز الدخول غير صحيح" });
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "100px" }}>
        <Card title="صفحة محمية" style={{ width: 400, textAlign: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", borderRadius: "8px" }}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <p style={{ fontSize: "16px", color: "#555" }}>يرجى إدخال رمز الدخول للوصول إلى هذه الصفحة</p>
            <Input.Password
              placeholder="أدخل رمز الدخول"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onPressEnter={handlePasswordSubmit}
              size="large"
              style={{ textAlign: "center" }}
            />
            <Button type="primary" size="large" onClick={handlePasswordSubmit} style={{ width: "100%", borderRadius: "6px" }}>
              دخول
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <Card title="إدارة سجلات الحضور" className="attendance-logs-card">
      <div className="filters-container" style={{ marginBottom: 20 }}>
        <Space wrap>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginLeft: 8 }}>اختر شهرًا: </span>
            <DatePicker
              picker="month"
              defaultValue={dayjs()}
              onChange={onMonthChange}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginLeft: 8 }}>أو اختر فترة: </span>
            <RangePicker
              value={[dayjs(start, "YYYY-MM-DD"), dayjs(end, "YYYY-MM-DD")]}
              onChange={onRangeChange}
            />
          </div>
          <Select
            showSearch
            style={{ width: 250 }}
            placeholder="البحث بتحديد الموظف"
            optionFilterProp="children"
            onChange={(val) => handleFilterChange("user_id", val)}
            allowClear
            value={filters.user_id}
          >
            {users.map((u) => (
              <Select.Option key={u.user_id} value={u.user_id}>
                {u.name}
              </Select.Option>
            ))}
          </Select>
          <Button
            type="success"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", color: "white" }}
          >
            إضافة سجل جديد
          </Button>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => fetchLogs()}
            loading={loading}
          >
            تحديث
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 15 }}
        scroll={{ x: 1000 }}
      />

      <Modal
        title="تفاصيل الإجازات لهذا السجل"
        open={vacDetailOpen}
        onCancel={() => setVacDetailOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setVacDetailOpen(false)}>
            إغلاق
          </Button>,
        ]}
        centered
        width={520}
      >
        {vacDetailPayload && (
          <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">الموظف: </Text>
              <Text strong>
                {vacDetailPayload.record.user?.name || vacDetailPayload.record.user_id}
              </Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">التاريخ: </Text>
              <Text strong>{vacDetailPayload.record.date}</Text>
            </div>
            {vacDetailPayload.matching.map((vac, idx) => {
              const vStatus = Number(vac.accepted) || 0;
              let vStatusText = "في انتظار الاعتماد";
              let vColor = "#8c8c8c";
              if (vStatus === 2) {
                vColor = "#0972B6";
                vStatusText = "معتمدة من الشؤون";
              } else if (vStatus === 1) {
                vColor = "#52c41a";
                vStatusText = "معتمدة من مدير الإدارة";
              } else if (vStatus === 3) {
                vColor = "#f87171";
                vStatusText = "مرفوضة من مدير الإدارة";
              } else if (vStatus === 4) {
                vColor = "#991b1b";
                vStatusText = "مرفوضة من الشؤون";
              }
              const typeLabel =
                pickVacationTypeLabel(vac, vacTypeLabelById) || "—";
              return (
                <Card
                  key={vac.id || idx}
                  size="small"
                  style={{
                    marginBottom: 12,
                    borderRight: `4px solid ${vColor}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <Text type="secondary">نوع الإجازة: </Text>
                      <Text strong style={{ fontSize: 15 }}>
                        {typeLabel}
                      </Text>
                    </div>
                    <Tag
                      color={
                        vColor === "#0972B6"
                          ? "blue"
                          : vColor === "#52c41a"
                            ? "green"
                            : "default"
                      }
                    >
                      {vStatusText}
                    </Tag>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </Modal>

      <Modal
        title={editingRecord ? "تعديل سجل" : "إضافة سجل جديد"}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        okText="حفظ"
        cancelText="إلغاء"
        destroyOnClose
        centered
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="user_id"
            label="الموظف"
            rules={[{ required: true, message: "يرجى اختيار الموظف" }]}
          >
            <Select showSearch optionFilterProp="children">
              {users.map((u) => (
                <Select.Option key={u.user_id} value={u.user_id}>
                  {u.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="date"
            label="التاريخ"
            rules={[{ required: true, message: "يرجى اختيار التاريخ" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="attendance_time" label="وقت الحضور">
            <DatePicker showTime style={{ width: "100%" }} format="YYYY-MM-DD HH:mm:ss" />
          </Form.Item>
          <Form.Item name="leave_time" label="وقت الانصراف">
            <DatePicker showTime style={{ width: "100%" }} format="YYYY-MM-DD HH:mm:ss" />
          </Form.Item>
          <Form.Item name="type" label="النوع">
            <Input placeholder="مثال: Fingerprint, Task, etc." />
          </Form.Item>
          <Form.Item name="canceled" label="الحالة">
            <Select>
              <Select.Option value={0}>نشط</Select.Option>
              <Select.Option value={1}>ملغى</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
